<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software: you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation, either
 * version 3 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with OrangeHRM.
 * If not, see <https://www.gnu.org/licenses/>.
 */
-->

<template>
  <div class="orangehrm-background-container">
    <div class="orangehrm-card-container">
      <oxd-text tag="h6" class="orangehrm-main-title">
        Work Sites
      </oxd-text>
      <oxd-divider />

      <oxd-table-filter filter-title="Work Sites">
        <oxd-form @submit-valid="filterItems">
          <oxd-form-actions>
            <oxd-button
              display-type="ghost"
              label="Add"
              @click="onClickAdd"
            />
          </oxd-form-actions>
        </oxd-form>
      </oxd-table-filter>

      <oxd-divider />

      <div class="orangehrm-container">
        <oxd-card-table
          v-model:selected="checkedItems"
          :headers="headers"
          :items="items?.data"
          :selectable="true"
          :clickable="false"
          :loading="isLoading"
          row-decorator="oxd-table-decorator-card"
        />
        <oxd-pagination
          v-if="showPaginator"
          v-model:current="currentPage"
          :length="pages"
        />
      </div>

      <oxd-divider />

      <oxd-form-actions>
        <oxd-button
          :disabled="!checkedItems.length"
          display-type="danger"
          label="Delete Selected"
          @click="onClickDeleteSelected"
        />
      </oxd-form-actions>
    </div>

    <delete-confirmation ref="deleteDialog" />
  </div>
</template>

<script>
import {computed, ref} from 'vue';
import useToast from '@/core/util/composable/useToast';
import {navigate} from '@ohrm/core/util/helper/navigation';
import {APIService} from '@/core/util/services/api.service';
import usePaginate from '@/core/util/composable/usePaginate';
import DeleteConfirmationDialog from '@ohrm/components/dialogs/DeleteConfirmationDialog';

const WorkSiteDataNormalizer = (data) => {
  return data.map((item) => {
    return {
      id: item.id,
      name: item.name,
      address: item.address ?? '—',
      coordinates: `${Number(item.latitude).toFixed(5)}, ${Number(item.longitude).toFixed(5)}`,
      radius: `${item.radiusMeters} m`,
      status: item.isActive ? 'Active' : 'Inactive',
    };
  });
};

export default {
  name: 'WorkSiteList',
  components: {
    'delete-confirmation': DeleteConfirmationDialog,
  },
  setup() {
    const {saveSuccess, deleteSuccess} = useToast();
    const http = new APIService(window.appGlobal.baseUrl, '/api/v2/attendance/work-sites');

    const {
      currentPage,
      total,
      pages,
      pageSize,
      response,
      isLoading,
      execQuery,
    } = usePaginate(http, {
      normalizer: WorkSiteDataNormalizer,
    });

    return {
      http,
      total,
      pages,
      pageSize,
      currentPage,
      isLoading,
      items: response,
      execQuery,
      saveSuccess,
      deleteSuccess,
    };
  },
  data() {
    return {
      headers: [
        {name: 'name', title: 'Name', style: {flex: 3}},
        {name: 'address', title: 'Address', style: {flex: 4}},
        {name: 'coordinates', title: 'Coordinates (Lat, Lng)', style: {flex: 3}},
        {name: 'radius', title: 'Geofence Radius', style: {flex: 2}},
        {name: 'status', title: 'Status', style: {flex: 1}},
        {
          name: 'actions',
          title: 'Actions',
          slot: 'action',
          style: {flex: 1},
          cellType: 'oxd-table-cell-actions',
          cellConfig: {
            delete: {
              onClick: this.onClickDelete,
              component: 'oxd-icon-button',
              props: {name: 'trash'},
            },
            edit: {
              onClick: this.onClickEdit,
              props: {name: 'pencil-fill'},
            },
          },
        },
      ],
      checkedItems: [],
    };
  },
  computed: {
    showPaginator() {
      return this.pages > 1;
    },
  },
  methods: {
    onClickAdd() {
      navigate('/attendance/saveWorkSite');
    },
    onClickEdit(item) {
      navigate('/attendance/saveWorkSite/{id}', {id: item.id});
    },
    async onClickDelete(item) {
      const confirmed = await this.$refs.deleteDialog.showDialog();
      if (confirmed === 'ok') {
        this.deleteItems([item.id]);
      }
    },
    async onClickDeleteSelected() {
      const confirmed = await this.$refs.deleteDialog.showDialog();
      if (confirmed === 'ok') {
        const ids = this.checkedItems.map((item) => item.id);
        this.deleteItems(ids);
      }
    },
    async deleteItems(ids) {
      if (ids && ids.length > 0) {
        await this.http.deleteAll({ids});
        await this.execQuery();
        this.deleteSuccess();
        this.checkedItems = [];
      }
    },
    async filterItems() {
      await this.execQuery();
    },
  },
};
</script>
